// Port of ios/Sources/ViewModels/MediaAutoLoad.swift.
//
// Whether a picture may fetch itself right now, or whether the link belongs to the words.
//
// "Let's work with the offline or low data mode" (user, 2026-09-12). A conversation's images are
// the overwhelming majority of its bytes: a screenshot is a hundred text messages. On a hotspot,
// a roaming SIM or a phone in Low Data Mode, a screenful of photos fetching themselves is what
// makes the NEXT message take ten seconds to send — the outbox and the pictures queue behind the
// same narrow link.
//
// So on a link the system itself calls expensive or constrained, media waits for a tap and text
// gets the bandwidth. Nothing is hidden: the card stays, says what it is, and loads on request.
//
// Deliberately NOT a setting: iOS's Low Data Mode is already per-network and Android's Data Saver
// is system-wide, and a second switch buried in this app would be one more thing to be wrong.
// The reader's answer is the tap. (On the web the link comes from the Network Information API —
// `navigator.connection.saveData` / `type === 'cellular'` — where the browser offers it, and is
// `unknown` where it does not.)
//
// Kotlin twin: `MediaAutoLoad` in `MediaAutoLoad.kt`.

/** What the platform says about the link right now. */
export interface MediaLink {
  /** Cellular, a personal hotspot, or any network the system bills by the byte. */
  isExpensive: boolean;
  /** Low Data Mode / Data Saver: the reader has ASKED for less traffic. */
  isConstrained: boolean;
}

/**
 * - `load`: fetch it now.
 * - `defer`: leave it for a tap: the link is expensive or the reader asked for less traffic.
 */
export type MediaAutoLoadDecision = 'load' | 'defer';

/**
 * What to assume before the first reading arrives: an unmeasured link is treated as free,
 * because guessing wrong here costs a slow image, while guessing the other way would put
 * every picture behind a tap on ordinary Wi-Fi.
 */
const unknown: MediaLink = Object.freeze({ isExpensive: false, isConstrained: false });

/**
 * - `isCached`: bytes already on disk — no network, so nothing to weigh.
 * - `requestedByReader`: they tapped this picture; their explicit answer beats the policy.
 */
function decide(link: MediaLink, isCached: boolean, requestedByReader = false): MediaAutoLoadDecision {
  // A cached image costs nothing to show and everything to hide.
  if (isCached) return 'load';
  if (requestedByReader) return 'load';
  if (link.isConstrained) return 'defer';
  if (link.isExpensive) return 'defer';
  return 'load';
}

/** Whether the conversation should say, once, that it is holding pictures back. */
function shouldExplain(link: MediaLink, deferredCount: number): boolean {
  return deferredCount > 0 && (link.isExpensive || link.isConstrained);
}

export const MediaAutoLoad = {
  unknown,
  decide,
  shouldExplain,
} as const;
